import { PRODUCTS_LISTING_FILTER_INITIAL_STATE } from "../constants";
import { PRODUCTS_FILTER_ACTIONS } from "../enums";
import { IProductsFilterState } from "../types";
import { getCountOfSelectedFilterValues } from "../utils";

export type ReducerAction<T, P> = {
  type: T;
  payload: P;
};

export type CollectionFilterActions =
  | ReducerAction<PRODUCTS_FILTER_ACTIONS.TOGGLE_NAVIGATION, void>
  | ReducerAction<
      PRODUCTS_FILTER_ACTIONS.SET_ACTIVE_DROPDOWN_FILTER,
      number | undefined
    >
  | ReducerAction<PRODUCTS_FILTER_ACTIONS.TOGGLE_MOBILE_DRAWER_MENU, void>
  | ReducerAction<PRODUCTS_FILTER_ACTIONS.SET_SORT_BY, string | undefined>
  | ReducerAction<PRODUCTS_FILTER_ACTIONS.SET_SELECTED_NAVIGATION, string>
  | ReducerAction<
      PRODUCTS_FILTER_ACTIONS.SET_FILTER,
      Partial<IProductsFilterState>
    >
  | ReducerAction<
      PRODUCTS_FILTER_ACTIONS.SET_FILTER_VALUES,
      { category: string; value: string }
    >
  | ReducerAction<PRODUCTS_FILTER_ACTIONS.RESET_FILTER, void>;

export const productsFilterReducer = (
  state: IProductsFilterState,
  action: CollectionFilterActions
): IProductsFilterState => {
  switch (action.type) {
    case PRODUCTS_FILTER_ACTIONS.TOGGLE_NAVIGATION:
      return {
        ...state,
        activeDropdownFilter: undefined,
        isNavigationOpen: !state.isNavigationOpen,
      };
    case PRODUCTS_FILTER_ACTIONS.SET_ACTIVE_DROPDOWN_FILTER:
      return {
        ...state,
        isNavigationOpen: false,
        activeDropdownFilter: action.payload,
      };
    case PRODUCTS_FILTER_ACTIONS.TOGGLE_MOBILE_DRAWER_MENU:
      return {
        ...state,
        showMobileDrawerMenu: !state.showMobileDrawerMenu,
      };
    case PRODUCTS_FILTER_ACTIONS.SET_SORT_BY:
      return {
        ...state,
        selectedSortBy: action.payload,
      };
    case PRODUCTS_FILTER_ACTIONS.SET_FILTER: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case PRODUCTS_FILTER_ACTIONS.SET_FILTER_VALUES:
      const { category, value } = action.payload;

      if (typeof category !== "string") return state;

      const selectedFilterValues = {
        ...state.selectedFilterValues,
      };
      if (selectedFilterValues?.price?.[0]?.includes("custom")) {
        selectedFilterValues[category] = [value];
      } else if (selectedFilterValues?.[category]?.includes(value)) {
        selectedFilterValues[category] = selectedFilterValues[category].filter(
          (filter) => filter !== value
        );
      } else {
        if (!selectedFilterValues[category]) {
          selectedFilterValues[category] = [];
        }
        selectedFilterValues[category] = [
          ...selectedFilterValues[category],
          value,
        ];
      }

      Object.keys(selectedFilterValues).forEach((category) => {
        if (selectedFilterValues[category].length === 0) {
          delete selectedFilterValues[category];
        }
      });

      return {
        ...state,
        selectedFilterValues,
        countSelectedFilterValues:
          getCountOfSelectedFilterValues(selectedFilterValues),
      };
    case PRODUCTS_FILTER_ACTIONS.SET_SELECTED_NAVIGATION:
      return {
        ...state,
        selectedNavigation: action.payload,
      };
    case PRODUCTS_FILTER_ACTIONS.RESET_FILTER:
      return PRODUCTS_LISTING_FILTER_INITIAL_STATE;
    default:
      return state;
  }
};

export const actionCreators = {
  toggleNavigation: (): CollectionFilterActions => ({
    type: PRODUCTS_FILTER_ACTIONS.TOGGLE_NAVIGATION,
    payload: undefined,
  }),
  setActiveDropdownFilter: (
    activeDropdownFilter: number | undefined
  ): CollectionFilterActions => ({
    type: PRODUCTS_FILTER_ACTIONS.SET_ACTIVE_DROPDOWN_FILTER,
    payload: activeDropdownFilter,
  }),
  toggleMobileDrawerMenu: (): CollectionFilterActions => ({
    type: PRODUCTS_FILTER_ACTIONS.TOGGLE_MOBILE_DRAWER_MENU,
    payload: undefined,
  }),
  setSortBy: (sortBy: string | undefined): CollectionFilterActions => ({
    type: PRODUCTS_FILTER_ACTIONS.SET_SORT_BY,
    payload: sortBy,
  }),
  setFilter: (
    filter: Partial<IProductsFilterState> = {}
  ): CollectionFilterActions => ({
    type: PRODUCTS_FILTER_ACTIONS.SET_FILTER,
    payload: filter,
  }),
  setFilterValues: (filter: {
    category: string;
    value: string;
  }): CollectionFilterActions => ({
    type: PRODUCTS_FILTER_ACTIONS.SET_FILTER_VALUES,
    payload: filter,
  }),
  setSelectedNavigation: (
    selectedNavigation: string
  ): CollectionFilterActions => ({
    type: PRODUCTS_FILTER_ACTIONS.SET_SELECTED_NAVIGATION,
    payload: selectedNavigation,
  }),
  resetFilter: (): CollectionFilterActions => ({
    type: PRODUCTS_FILTER_ACTIONS.RESET_FILTER,
    payload: undefined,
  }),
};
